import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { auth, db } from '../../services/firebase';

const COLLECTION = 'carePlans';

export const subscribeCarePlans = (childId = '', onChange = () => {}, onError = () => {}) => {
  if (!childId) {
    onChange([]);
    return () => {};
  }
  return onSnapshot(
    query(collection(db, COLLECTION), where('childId', '==', childId)),
    (snapshot) => onChange(snapshot.docs
      .map((item) => ({ id: item.id, ...item.data() }))
      .filter((item) => !item.removedAt)
      .sort((left, right) => Number(right.updatedAt?.toMillis?.() || 0) - Number(left.updatedAt?.toMillis?.() || 0))),
    onError
  );
};

export const subscribeCarePlanReviews = (childId = '', onChange = () => {}, onError = () => {}) => {
  if (!childId) { onChange([]); return () => {}; }
  return onSnapshot(
    query(collection(db, 'carePlanReviews'), where('childId', '==', childId)),
    (snapshot) => onChange(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).filter((item) => item.status === 'pending')),
    onError
  );
};

export const createCarePlan = async (childId = '', values = {}) => addDoc(collection(db, COLLECTION), {
  childId,
  title: String(values.title || '').trim(),
  topicKey: String(values.title || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'),
  status: values.status || 'suggested',
  targetDateText: String(values.targetDateText || '').trim(),
  tentative: Boolean(values.tentative),
  sourceId: null,
  sourceContributions: [],
  createdBy: auth.currentUser?.uid || null,
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
  removedAt: null,
});

export const updateCarePlan = async (planId = '', values = {}) => updateDoc(doc(db, COLLECTION, planId), {
  ...values,
  updatedAt: serverTimestamp(),
  updatedBy: auth.currentUser?.uid || null,
});

export const removeCarePlan = (planId = '') => updateCarePlan(planId, {
  removedAt: serverTimestamp(),
  removedBy: auth.currentUser?.uid || null,
});

export const restoreCarePlan = (planId = '') => updateCarePlan(planId, {
  removedAt: null,
  removedBy: null,
});

export const resolveCarePlanReview = async (review = {}, decision = 'accept') => {
  const batch = writeBatch(db);
  const resolvedAt = Timestamp.now();
  if (decision === 'accept' && review.existingPlanId) {
    batch.update(doc(db, COLLECTION, review.existingPlanId), {
      ...(review.proposed || {}),
      lastAcceptedSourceId: review.sourceId || null,
      sourceContributions: arrayUnion({
        sourceId: review.sourceId || null,
        sourceText: review.proposed?.sourceText || '',
        addedAt: resolvedAt,
      }),
      planHistory: arrayUnion({
        sourceId: review.sourceId || null,
        previous: review.current || {},
        changedAt: resolvedAt,
      }),
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || null,
    });
  }
  batch.update(doc(db, 'carePlanReviews', review.id), {
    status: decision === 'accept' ? 'accepted' : 'dismissed',
    resolvedAt: serverTimestamp(),
    resolvedBy: auth.currentUser?.uid || null,
  });
  await batch.commit();
};
